import { useEffect, useSyncExternalStore } from 'react'

/**
 * ══ ĐIỀU HƯỚNG TRONG ỨNG DỤNG — `pushState` + `popstate`, KHÔNG tải lại trang (T2, soát 16/10) ═════
 *
 * Bản cũ chạy `react-router` (`proship-trans/src/components/Layout.tsx:246-276` — `<NavLink>`,
 * `useNavigate()`): bấm menu là đổi màn tại chỗ, khoảng 0,1 giây, không nháy, kho cache còn nguyên,
 * sidebar thu gọn vẫn thu gọn. rs-erp trước đây đi mọi ngả bằng `location.assign` ⇒ tải lại CẢ TRANG
 * (0,5–1 giây, nháy "Đang tải…", gọi lại 160–280 request, `QueryClient` dựng lại, sidebar bung ra).
 *
 * ⚠ CỐ Ý KHÔNG kéo `react-router` vào: router của kho là MỘT bảng chọn màn theo `location.pathname`
 *   (`app/router.tsx`). Thứ còn thiếu chỉ là "đổi URL rồi vẽ lại" — vài chục dòng dưới đây, không
 *   đáng một thư viện trong gói khởi tạo (cổng ~200 KB).
 *
 * ⭐ HAI MẢNH:
 *   · `navigate(to)` — ghi lịch sử (`pushState`/`replaceState`) rồi báo cho router vẽ lại.
 *   · `useNavKey()` — router đăng ký nghe (cả Back/Forward = `popstate`), đọc lại `location.pathname`
 *     và lấy KHOÁ của màn: khoá đổi ⇒ màn DỰNG LẠI từ đầu.
 *
 * ⚠ VÌ SAO DỰNG LẠI MÀN mỗi lần `navigate()`: các màn đọc `?open=` · `?tab=` · `?stmt=`… MỘT LẦN lúc
 *   dựng (`useState(() => new URLSearchParams(location.search)…)`). Chuông bấm `/approvals?open=dntt-B`
 *   trong lúc đang đứng ở `/approvals` mà không dựng lại thì phiếu B không bao giờ mở. Trước đây việc
 *   này "tự đúng" nhờ tải lại trang; nay khoá màn theo lượt điều hướng để giữ đúng hành vi đó — còn
 *   khung (sidebar, header, chuông) và kho cache thì GIỮ NGUYÊN.
 * ⚠ Back/Forward CÙNG đường dẫn (chỉ khác `?…`) thì KHÔNG dựng lại: đó là lịch sử do chính màn ghi
 *   (Vỏ lạnh ghi khối đang mở bằng `pushState`, đúng bản cũ) — màn tự nghe `popstate` mà đổi khối;
 *   dựng lại thì mất bộ lọc và nháy "Đang tải…". Khác đường dẫn thì đương nhiên là màn khác.
 */

const EVENT = 'rs:navigate'
/** Tăng ở MỌI thay đổi (kể cả `popstate`) — chỉ để router vẽ lại. */
let version = 0
/** Tăng CHỈ khi `navigate()` — phần của khoá màn (xem khối chú thích đầu tệp). */
let navCount = 0

const bump = (): void => {
  version += 1
  navCount += 1
  window.dispatchEvent(new Event(EVENT))
}

const subscribe = (onChange: () => void): (() => void) => {
  // Back/Forward của trình duyệt — tăng số rồi mới báo, để `getSnapshot` thấy giá trị mới.
  const onPop = (): void => { version += 1; onChange() }
  window.addEventListener('popstate', onPop)
  window.addEventListener(EVENT, onChange)
  return () => {
    window.removeEventListener('popstate', onPop)
    window.removeEventListener(EVENT, onChange)
  }
}

const getVersion = (): number => version

/**
 * Router nghe chỗ này: mỗi lần điều hướng thì vẽ lại, và trả KHOÁ của màn = đường dẫn + lượt
 * `navigate()`. Đọc biến mô-đun SAU `useSyncExternalStore` — lượt vẽ lại nào cũng do kho này kích.
 */
export const useNavKey = (): string => {
  useSyncExternalStore(subscribe, getVersion, getVersion)
  return `${location.pathname}#${String(navCount)}`
}

/**
 * ⭐ BACK/FORWARD GIỮA HAI URL CÙNG ĐƯỜNG DẪN — màn tự đổi hộp theo tham số (rà Lô 2, 17/10).
 *
 * `useNavKey` CỐ Ý không dựng lại màn khi Back/Forward chỉ đổi `?…` (xem đầu tệp — Vỏ lạnh). Hệ quả: chuông mở
 * `/approvals?open=dntt-B` khi đang đứng ở `/approvals`, rồi bấm Back về `/approvals` ⇒ hộp phiếu B VẪN MỞ; Forward thì
 * không mở lại. Màn nào mở hộp theo một tham số thì gọi hook này: mỗi `popstate` đọc lại tham số đó và giao cho màn
 * (`null` = URL không còn tham số ⇒ màn đóng hộp). Không động vào khoá màn ⇒ Vỏ lạnh và các màn khác không đổi gì.
 * ⚠ `apply` nên là hàm đặt state (đặt cùng giá trị thì React bỏ qua) — hook đăng ký lại mỗi khi `apply` đổi.
 */
export const usePopParam = (name: string, apply: (value: string | null) => void): void => {
  useEffect(() => {
    const onPop = (): void => { apply(new URLSearchParams(location.search).get(name)) }
    window.addEventListener('popstate', onPop)
    return () => { window.removeEventListener('popstate', onPop) }
  }, [name, apply])
}

/** URL hiện tại dạng `/duong-dan?tham=so#neo` — so với đích để biết có phải đi đâu không. */
const currentUrl = (): string => `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`

/**
 * Đích này có đi được BÊN TRONG ứng dụng không, và nếu có thì là đường dẫn tương đối nào.
 *
 * ⚠ TRẢ `null` (đi bằng trình duyệt, tải trang thật) cho ba loại:
 *   · KHÁC NGUỒN GỐC (trang FADO SSO, Google Drive…) — không phải màn của mình;
 *   · `/api/...` — luồng OIDC (`/api/v1/auth/sso/start`) và tải tệp là điều hướng trình duyệt THẬT;
 *   · đường dẫn có ĐUÔI TỆP (`/print/logo-ratraco.png`, `/RT-LOGO.jpg`) — tệp tĩnh, không phải màn.
 */
export const internalPath = (to: string): string | null => {
  const base = typeof location.href === 'string' && location.href !== '' ? location.href : 'http://localhost/'
  let url: URL
  try { url = new URL(to, base) } catch { return null }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
  const origin = typeof location.origin === 'string' && location.origin !== '' && location.origin !== 'null'
    ? location.origin : new URL(base).origin
  if (url.origin !== origin) return null
  if (url.pathname === '/api' || url.pathname.startsWith('/api/')) return null
  const last = url.pathname.split('/').pop() ?? ''
  if (/\.[a-z0-9]{2,5}$/i.test(last)) return null
  return `${url.pathname}${url.search}${url.hash}`
}

/**
 * ⭐ HÀM ĐIỀU HƯỚNG CHUNG — mọi chỗ trước đây gọi `location.assign('/…')` nội bộ đi qua đây.
 *
 * `replace: true` = thay mục lịch sử hiện tại (đá về trang gốc, bỏ tham số đã đọc) — Back không
 * quay lại chỗ vừa bị đá đi, đúng `<Navigate replace>` của bản cũ (`App.tsx:63`).
 *
 * ⚠ Cùng URL với chỗ đang đứng thì KHÔNG làm gì — bản cũ (`<NavLink>`) bấm lại mục đang mở cũng
 *   không làm gì; dựng lại màn ở đây là mất bộ lọc người dùng đang gõ dở.
 */
export const navigate = (to: string, opts: { replace?: boolean } = {}): void => {
  const path = internalPath(to)
  if (path === null) { location.assign(to); return }
  if (path === currentUrl()) return
  if (opts.replace === true) history.replaceState(null, '', path)
  else history.pushState(null, '', path)
  bump()
}

/**
 * ══ BẮT CÚ BẤM VÀO `<a href="/…">` NỘI BỘ ════════════════════════════════════════════════════════
 * Mấy chục chỗ trong các màn dùng thẻ `<a href="/approvals">`, `<a href="/chung-tu?open=…">` — mỗi cú
 * bấm là một lần tải lại trang. Thay từng chỗ bằng `onClick` là vài chục sửa đổi rải rác dễ sót;
 * một bộ nghe ở `document` (đúng cách `<Link>` của react-router làm cho từng thẻ) bắt hết một lần.
 *
 * ⚠ KHÔNG CƯỚP — trình duyệt tự xử như thường:
 *   · Ctrl/Cmd/Shift/Alt+click, chuột giữa (mở tab/cửa sổ mới — thói quen kế toán đối chiếu hai màn);
 *   · thẻ có `target` khác `_self` (`target="_blank"` mở tab mới đúng ý người viết), có `download`;
 *   · cú bấm mà màn đã `preventDefault()` (VD `NavLeaf` tự điều hướng);
 *   · đích không đi được bên trong (`internalPath` trả `null`).
 */
export const shouldIntercept = (e: MouseEvent, a: HTMLAnchorElement): string | null => {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null
  const target = a.getAttribute('target')
  if (target !== null && target !== '' && target !== '_self') return null
  if (a.hasAttribute('download')) return null
  const href = a.getAttribute('href')
  if (href === null || href === '' || href.startsWith('#')) return null
  return internalPath(href)
}

export const useInternalLinks = (): void => {
  useEffect(() => {
    const onClick = (e: MouseEvent): void => {
      const el = e.target instanceof Element ? e.target.closest('a') : null
      if (el === null) return
      const path = shouldIntercept(e, el)
      if (path === null) return
      e.preventDefault()
      navigate(path)
    }
    document.addEventListener('click', onClick)
    return () => { document.removeEventListener('click', onClick) }
  }, [])
}

/**
 * Đá sang chỗ khác SAU KHI vẽ (thay cho `location.replace(...)` gọi ngay trong lúc render — kiểu cũ
 * chỉ chạy được vì nó tải lại trang). Gọi `navigate` trong lúc render là cập nhật một kho bên ngoài
 * giữa chừng một lượt vẽ — React cảnh báo và có thể vẽ lặp.
 */
export const useRedirect = (to: string): void => {
  useEffect(() => { navigate(to, { replace: true }) }, [to])
}

/**
 * Điều hướng từ một cú bấm trên NÚT (không phải thẻ `<a>`): Ctrl/Cmd/Shift+click hay chuột giữa thì
 * mở TAB MỚI — đúng điều trình duyệt làm với một liên kết, và đúng thói quen "mở màn ra tab riêng để
 * đối chiếu" mà bản cũ phục vụ có chủ đích (`Layout.tsx:302-320`). Click trái trơn thì `navigate`.
 */
export const navigateFromClick = (
  e: { button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean },
  to: string,
): void => {
  if (e.button === 1 || e.metaKey || e.ctrlKey || e.shiftKey) { window.open(to, '_blank', 'noopener'); return }
  navigate(to)
}
